import type { SnowplowIds } from '@/services/wapi'

/** The tracker namespace `snowplow-script.tsx` registers via `newTracker`. */
const TRACKER_NAME = 'cf'

/**
 * Long enough to cover tracker init on a cold, throttled connection; short enough that a
 * visitor with Snowplow blocked outright doesn't keep a timer alive.
 */
const TRACKER_TIMEOUT_MS = 5000

/**
 * How often to check for the command queue's *existence*. `next/script` loads the
 * bootstrap `afterInteractive`, so a caller that runs on mount can arrive before it —
 * short enough to add no perceptible delay once it lands.
 */
const QUEUE_POLL_INTERVAL_MS = 50

/** The tracker instances exposed as `this` inside a `snowplow(function(){…})` callback. */
type SnowplowTrackers = Record<string, { getDomainUserInfo?: () => unknown[] } | undefined>

/** A present-but-empty cookie (`sp=`) carries no identity, so it reads as absent. */
function emptyToUndefined(value: string | undefined): string | undefined {
    return value === undefined || value === '' ? undefined : value
}

/**
 * The network user id is set **server-side by the collector**, so the tracker doesn't own
 * it and exposes no getter — the cookie is the only source. ui-react reads it the same way.
 */
function readNetworkUserId(): string | undefined {
    if (typeof document === 'undefined') {
        return undefined
    }
    const cookie = document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith('sp='))
    return emptyToUndefined(cookie?.split('=')[1])
}

/**
 * Best-effort synchronous read of the two Snowplow IDs, straight from their cookies.
 *
 * Use `waitForSnowplowIds` instead wherever you can await — it asks the tracker for the
 * domain id rather than parsing its cookie. This exists for callers that cannot await.
 *
 * `_sp_id.<siteId>` value format (dot-separated):
 *   domainUserId.createTs.visitCount.nowTs.lastVisitTs.sessionId
 *
 * Both fields read as absent when their cookie is missing or empty — neither is set on a
 * first visit before the tracker initializes.
 *
 * @see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/web-tracker/cookies-and-local-storage/
 */
export function readSnowplowIds(): SnowplowIds {
    if (typeof document === 'undefined') {
        return {}
    }

    const domainCookie = document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.startsWith('_sp_id.'))

    return {
        sp_domain_id: emptyToUndefined(domainCookie?.split('=')[1]?.split('.')[0]),
        sp_network_id: readNetworkUserId(),
    }
}

/**
 * Resolves the Snowplow IDs once the tracker is ready, asking the tracker for the domain
 * id instead of parsing its cookie.
 *
 * The bootstrap snippet defines `window.snowplow` synchronously as a command queue before
 * `sp.js` loads, and the queue runs pushed callbacks once the tracker initializes — so
 * pushing one *is* the readiness signal, with no assumption about the `_sp_id` cookie's
 * name or field order. The bootstrap itself loads `afterInteractive`, though, so the queue
 * may not exist yet when a caller runs on mount — its arrival is polled for within the
 * same deadline rather than treated as "no tracker".
 *
 * Resolves `null` when the tracker never initializes within `timeoutMs` (its queue never
 * appears, it is blocked, or the script failed) or reports no domain id. Callers must treat
 * that as "don't send": `sp_domain_id` *is* the identifier, and an event without it can't
 * be stitched.
 *
 * `sp_network_id` is read from its cookie and never blocks — the collector sets it
 * server-side, so the tracker has no getter for it, and it's optional for our purposes.
 */
export function waitForSnowplowIds({
    timeoutMs = TRACKER_TIMEOUT_MS,
}: { timeoutMs?: number } = {}): Promise<SnowplowIds | null> {
    if (typeof window === 'undefined') {
        return Promise.resolve(null)
    }

    return new Promise<SnowplowIds | null>((resolve) => {
        let settled = false

        const settle = (ids: SnowplowIds | null) => {
            if (settled) {
                return
            }
            settled = true
            clearTimeout(timer)
            clearInterval(poll)
            resolve(ids)
        }

        const timer = setTimeout(() => settle(null), timeoutMs)

        const askTracker = () => {
            window.snowplow?.(function (this: SnowplowTrackers) {
                // The tracker API prepends a first-session flag, so the domain id is at
                // [1] — one further along than in the raw cookie. Do not copy cookie
                // indices here.
                const info = this[TRACKER_NAME]?.getDomainUserInfo?.()
                const domainUserId = Array.isArray(info) ? info[1] : undefined

                settle(
                    typeof domainUserId === 'string' && domainUserId !== ''
                        ? { sp_domain_id: domainUserId, sp_network_id: readNetworkUserId() }
                        : null,
                )
            })
        }

        const poll = setInterval(() => {
            if (typeof window.snowplow === 'function') {
                clearInterval(poll)
                askTracker()
            }
        }, QUEUE_POLL_INTERVAL_MS)

        if (typeof window.snowplow === 'function') {
            clearInterval(poll)
            askTracker()
        }
    })
}
